import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getAdmissionDischargeRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface AdmissionDischargeRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function AdmissionDischargeRow({
  time,
  observation,
  showNumbersOnly,
  flowsheetId,
  showPreviousVersions = true,
  isViewOnly = false,
}: AdmissionDischargeRowProps) {
  const { type, hasAnswers, hasNotes, notes } = useMemo(
    () => getAdmissionDischargeRowValues(observation),
    [observation],
  );

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  const previousVersions = hasPreviousVersions(observation);
  const showEditLaborStage =
    flowsheetId !== "clientPostpartum" && flowsheetId !== "newbornPostpartum";

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Admission & Discharge:</TextWrapper>
            <List fz="sm" withPadding>
              {type && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Type:
                    </TextWrapper>
                    <TextWrapper size="sm">{type}</TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Admission & Discharge Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={AdmissionDischargeRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && showEditLaborStage && !isViewOnly && (
        <ActionCell rowSpan={1} observation={observation} />
      )}
    </Table.Tr>
  );
}
